<template>
  <div id="app" class="min-h-screen bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-300 transition-colors duration-200">
    <router-view :key="$route.fullPath" />
  </div>
</template>

<script>
import dayjs from "dayjs";

export default {
  name: "App",
  async mounted() {
    this.setupDateFormatObserver();
  },
  methods: {
    setupDateFormatObserver() {
      const formatInput = (el) => {
        if (el.value) {
          el.setAttribute("data-date", dayjs(el.value).format("DD/MM/YYYY"));
        } else {
          el.setAttribute("data-date", "ວວ/ດດ/ປປປປ");
        }
      };

      // Intercept programmatic value changes (e.g., from v-model)
      const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value");
      const originalSet = descriptor.set;
      Object.defineProperty(HTMLInputElement.prototype, "value", {
        set(val) {
          originalSet.call(this, val);
          if (this.type === "date") {
            formatInput(this);
          }
        },
        get() {
          return descriptor.get.call(this);
        }
      });

      // Format all existing
      document.querySelectorAll('input[type="date"]').forEach(formatInput);

      // Listen for user changes
      document.addEventListener("input", (e) => {
        if (e.target && e.target.type === "date") {
          formatInput(e.target);
        }
      });
      document.addEventListener("change", (e) => {
        if (e.target && e.target.type === "date") {
          formatInput(e.target);
        }
      });

      // Observer for newly added elements
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              if (node.tagName === "INPUT" && node.type === "date") {
                formatInput(node);
              }
              if (node.querySelectorAll) {
                const inputs = node.querySelectorAll('input[type="date"]');
                inputs.forEach(formatInput);
              }
            }
          });
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  },
};
</script>

<style lang="postcss">
@font-face {
  font-family: "NotoSansLao";
  src: url("../public/NotoSansLao.ttf") format("truetype");
}
*,
#app,
.swal2-popup,
.swal2-title,
.swal2-confirm,
.swal2-cancel {
  font-family: NotoSansLao;
}

/* Prevent SweetAlert from affecting app height */
body.swal2-shown {
  overflow: hidden !important;
  height: 100vh !important;
}

body.swal2-shown #app {
  height: 100vh !important;
  overflow: hidden !important;
}

.swal2-container,
.swal2-container-custom {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  z-index: 9999 !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.swal2-popup {
  position: relative !important;
  margin: 0 !important;
  top: auto !important;
  transform: none !important;
}

.modal {
  @apply fixed inset-0 bg-black bg-opacity-50 z-50;
}
.modal-background {
  @apply fixed bg-black bg-opacity-50 min-h-screen flex items-center justify-center inset-0 z-40;
}
input.text-input, select.text-input {
  @apply border border-gray-300 rounded-md p-2 w-full mb-4;
}

table {
  @apply w-full border-collapse mb-4;
}
th, td {
  @apply border border-gray-300 p-2 text-left;
}
th {
  @apply bg-gray-100 text-center font-bold;
}

/* Color Scheme & Dark Mode */
:root {
  color-scheme: light dark;
}

:root:has(> head > meta[name="color-scheme"][content="dark"]),
html.dark {
  color-scheme: dark;
}

:root:has(> head > meta[name="color-scheme"][content="light"]),
html:not(.dark) {
  color-scheme: light;
}

/* Base dark mode page background and text */
html.dark,
html.dark body,
html.dark #app {
  background-color: #0b1120 !important;
  color: #cbd5e1;
}

/* 1. All non-input elements with bg-white / bg-gray-50 / bg-slate-50 turn dark */
html.dark .bg-white:not(input):not(select):not(textarea):not(.student-id-card):not(.student-id-card *),
html.dark .bg-slate-50,
html.dark .bg-gray-50 {
  background-color: #1e293b !important;
}

/* Secondary dark backgrounds (headers, rows, inner cards) */
html.dark .bg-gray-100,
html.dark .bg-slate-100 {
  background-color: #0f172a !important;
}

/* Borders in dark mode */
html.dark .border-gray-50,
html.dark .border-gray-100,
html.dark .border-gray-200,
html.dark .border-gray-300,
html.dark .border-slate-100,
html.dark .border-slate-200,
html.dark .border-slate-300,
html.dark .divide-gray-50,
html.dark .divide-gray-100,
html.dark .divide-gray-200,
html.dark .divide-gray-300 {
  border-color: #334155 !important;
}

/* Hover states for cards, list items, and table rows */
html.dark .hover\:bg-gray-50:hover,
html.dark .hover\:bg-slate-50:hover,
html.dark .hover\:bg-gray-100:hover,
html.dark .hover\:bg-slate-100:hover {
  background-color: #293548 !important;
}

/* White buttons adapt to dark mode */
html.dark button.bg-white,
html.dark a.bg-white {
  background-color: #1e293b !important;
  color: #cbd5e1 !important;
  border-color: #475569 !important;
}
html.dark button.bg-white:hover,
html.dark a.bg-white:hover {
  background-color: #334155 !important;
  color: #e2e8f0 !important;
}

/* 2. Text colors on dark surfaces - soft gray for easy reading */
html.dark h1,
html.dark h2,
html.dark h3,
html.dark h4,
html.dark h5,
html.dark h6 {
  color: #e2e8f0;
}

html.dark p,
html.dark label,
html.dark .text-gray-900,
html.dark .text-slate-900 {
  color: #e2e8f0 !important;
}

html.dark .text-gray-800,
html.dark .text-slate-800,
html.dark .text-gray-700,
html.dark .text-slate-700,
html.dark .text-gray-600,
html.dark .text-slate-600 {
  color: #cbd5e1 !important;
}

html.dark .text-gray-500,
html.dark .text-slate-500 {
  color: #94a3b8 !important;
}

html.dark .text-gray-400,
html.dark .text-slate-400 {
  color: #64748b !important;
}

/* Preserve pure white on colored badges/buttons */
html.dark .text-white {
  color: #ffffff !important;
}

/* Table styles */
html.dark table {
  border-color: #334155 !important;
}
html.dark th {
  background-color: #0f172a !important;
  color: #e2e8f0 !important;
  border-color: #334155 !important;
}
html.dark td {
  border-color: #334155 !important;
  color: #cbd5e1 !important;
}

/* 3. INPUTS IN DARK MODE MUST HAVE WHITE BACKGROUND */
html.dark input:not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"]),
html.dark select,
html.dark textarea,
html.dark input.text-input,
html.dark select.text-input {
  background-color: #ffffff !important;
  color: #1f2937 !important;
  border-color: #cbd5e1 !important;
}

html.dark input:not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"]):focus,
html.dark select:focus,
html.dark textarea:focus {
  background-color: #ffffff !important;
  color: #111827 !important;
  border-color: #f59e0b !important;
}

html.dark input::placeholder,
html.dark textarea::placeholder {
  color: #6b7280 !important;
}

/* Date Input in dark mode (on white bg) */
html.dark input[type="date"]:not(:focus)::before {
  color: #374151 !important;
}

/* SweetAlert Dark Mode */
html.dark .swal2-popup {
  background: #1e293b !important;
  color: #cbd5e1 !important;
  border: 1px solid #334155 !important;
}
html.dark .swal2-title {
  color: #e2e8f0 !important;
}
html.dark .swal2-html-container {
  color: #94a3b8 !important;
}

/* Sidebar navigation dark mode */
html.dark aside nav a:not([class*="from-primary-500"]):not([class*="bg-primary-50"]),
html.dark aside nav button:not([class*="from-primary-500"]) {
  color: #94a3b8;
}

html.dark aside nav a:not([class*="from-primary-500"]):not([class*="bg-primary-50"]):hover,
html.dark aside nav button:not([class*="from-primary-500"]):hover {
  background-color: #334155;
  color: #e2e8f0;
}

html.dark aside nav a[class*="bg-primary-50"] {
  background-color: rgba(218, 37, 28, 0.2);
  color: #fca5a5;
}

/* Mobile Headers with blur & Student Bottom Navigation */
html.dark header[class*="bg-slate-50"],
html.dark header[class*="bg-white"] {
  background-color: rgba(15, 23, 42, 0.9) !important;
  border-color: #334155 !important;
}

html.dark nav[class*="bg-primary-500"] {
  background-color: #0f172a !important;
  border-top-color: #1e293b !important;
}

html.dark .border-white {
  border-color: #1e293b !important;
}

html.dark button.bg-slate-100,
html.dark div.bg-slate-100 {
  background-color: #334155 !important;
  color: #cbd5e1 !important;
}

/* Date Input dd/mm/yyyy formatting */
input[type="date"]:not(:focus) {
  position: relative;
  color: transparent !important;
}

input[type="date"]:not(:focus)::-webkit-datetime-edit {
  color: transparent !important;
}

input[type="date"]:not(:focus)::before {
  content: attr(data-date);
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #374151;
  pointer-events: none;
}
</style>
